import { useEffect, useRef, useState } from "react";
import { VAGUS_BY_LANG, tr } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Wind, Play, ChevronRight, CheckCircle2, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export function VagusView({ onNavigate }) {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);
  const steps = VAGUS_BY_LANG[lang] || VAGUS_BY_LANG.pl;
  const [i, setI] = useState(-1); // -1 = not started
  const [remaining, setRemaining] = useState(0);
  const tickRef = useRef(null);

  useEffect(() => () => tickRef.current && clearInterval(tickRef.current), []);

  const start = () => {
    setI(0);
    setRemaining(steps[0].duration);
    tickRef.current && clearInterval(tickRef.current);
    tickRef.current = setInterval(() => setRemaining(r => Math.max(0, r - 1)), 1000);
  };

  const next = () => {
    const ni = i + 1;
    if (ni >= steps.length) {
      tickRef.current && clearInterval(tickRef.current);
      setI(steps.length);
      return;
    }
    setI(ni);
    setRemaining(steps[ni].duration);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="label-cap text-amber-500">{t("vag.eyebrow")}</div>
      <h2 className="serif text-4xl text-stone-100 mt-2">{t("vag.title")}</h2>
      <p className="text-stone-400 mt-2">{t("vag.subtitle")}</p>

      {i === -1 && (
        <div className="mt-10 paper p-8 text-center">
          <Wind className="mx-auto text-amber-500" size={32}/>
          <div className="mt-4 space-y-2">
            {steps.map((s, idx) => (
              <div key={idx} className="mono text-[12px] text-stone-400 tracking-wide">
                {String(idx+1).padStart(2, "0")} · {s.title} <span className="text-stone-600">· {s.duration}s</span>
              </div>
            ))}
          </div>
          <button data-testid="vagus-start-btn" onClick={start} className="pill-btn mt-8"><Play size={14}/> {t("vag.start")}</button>
        </div>
      )}

      {i >= 0 && i < steps.length && (
        <AnimatePresence mode="wait">
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4 }}
            className="mt-10 paper p-8"
            data-testid={`vagus-step-${i}`}
          >
            <div className="mono text-[11px] tracking-[0.35em] text-amber-500 uppercase">{t("vag.step")} {String(i+1).padStart(2,"0")} / {String(steps.length).padStart(2,"0")}</div>
            <h3 className="serif text-3xl text-stone-100 mt-2">{steps[i].title}</h3>
            <p className="text-stone-300 leading-relaxed mt-4">{steps[i].body}</p>
            <div className="mt-8 flex items-center justify-between">
              <div className="mono text-4xl text-amber-400 tabular-nums">{remaining}s</div>
              <button data-testid="vagus-next-btn" onClick={next} className="pill-btn">
                <ChevronRight size={14}/> {i === steps.length - 1 ? t("vag.done") : t("vag.next")}
              </button>
            </div>
          </motion.div>
        </AnimatePresence>
      )}

      {i >= steps.length && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-10 paper p-10 text-center border-emerald-500/30">
          <CheckCircle2 className="mx-auto text-emerald-400" size={40}/>
          <div className="serif text-2xl text-stone-100 mt-4">{lang === "pl" ? "Gotowe. Możesz wejść w Craft & Truth." : "Done. You may enter Craft & Truth."}</div>
          <button data-testid="vagus-restart-btn" onClick={()=>setI(-1)} className="ghost-btn mt-6">{lang === "pl" ? "Jeszcze raz" : "Once more"}</button>
        </motion.div>
      )}

      {onNavigate && (
        <div className="mt-8 pt-6 border-t border-stone-800/60 flex justify-end">
          <button data-testid="nav-to-rehearsal-btn" onClick={() => onNavigate("rehearsal")} className="pill-btn">
            {tr("nav.to_rehearsal", lang)} <ArrowRight size={14}/>
          </button>
        </div>
      )}
    </div>
  );
}
